import IntranetLayout from "@/components/IntranetLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import {
  Users, Search, MapPin, Phone, Briefcase, Filter, Globe, X, Mail, Clock,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import OrgChart from "@/components/OrgChart";

interface DirectoryEntry {
  id: string;
  user_id: string;
  avatar_url: string | null;
  job_title: string;
  department: string;
  location: string | null;
  phone: string | null;
  bio: string | null;
  timezone: string | null;
  is_visible: boolean;
  full_name: string;
  email: string | null;
  isOnboarded: boolean;
}

const getLocalTime = (tz: string | null) => {
  if (!tz) return null;
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(new Date());
  } catch {
    return null;
  }
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

const EmployeeDirectory = () => {
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [selectedPerson, setSelectedPerson] = useState<DirectoryEntry | null>(null);

  const { data: directory = [], isLoading } = useQuery({
    queryKey: ["employee-directory"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_directory")
        .select("*")
        .eq("is_visible", true)
        .order("department")
        .order("job_title");
      if (error) throw error;

      const userIds = data.map((d) => d.user_id);
      let nameMap = new Map<string, string>();
      let onboardedSet = new Set<string>();

      if (userIds.length > 0) {
        const [profilesRes, completionsRes] = await Promise.all([
          supabase.from("profiles").select("user_id, full_name").in("user_id", userIds),
          supabase.from("onboarding_completions").select("user_id").eq("passed", true).in("user_id", userIds),
        ]);
        nameMap = new Map(profilesRes.data?.map((p) => [p.user_id, p.full_name || "Unknown"]) || []);
        onboardedSet = new Set(completionsRes.data?.map((c) => c.user_id) || []);
      }

      return data.map((d) => ({
        ...d,
        full_name: nameMap.get(d.user_id) || "Unknown",
        email: null as string | null,
        timezone: (d as any).timezone || null,
        isOnboarded: onboardedSet.has(d.user_id),
      })) as DirectoryEntry[];
    },
  });

  const allDepartments = [...new Set(directory.map((d) => d.department))].sort();

  const filtered = directory.filter((d) => {
    const q = search.toLowerCase();
    const matchSearch =
      d.full_name.toLowerCase().includes(q) ||
      d.department.toLowerCase().includes(q) ||
      d.job_title.toLowerCase().includes(q) ||
      (d.location ?? "").toLowerCase().includes(q);
    const matchDept = !filterDept || d.department === filterDept;
    return matchSearch && matchDept;
  });

  const departments = [...new Set(filtered.map((d) => d.department))];

  const directoryBody = (
    <>
      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-col sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, role, department, or location..."
            className="pl-10"
          />
        </div>
        <Select value={filterDept || "all"} onValueChange={(v) => setFilterDept(v === "all" ? "" : v)}>
          <SelectTrigger className="min-w-[200px]">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {allDepartments.map((dept) => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl text-foreground">Org Chart & Directory</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Velocity Offshore Holdings · Rev 1.0 · click any seat or person for full details
          </p>
        </div>
        <Badge variant="secondary" className="font-body text-xs">
          {filtered.length} {filtered.length === 1 ? "member" : "members"}
        </Badge>
      </div>

      <Tabs defaultValue="org" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="org">Org Chart</TabsTrigger>
          <TabsTrigger value="dir">Directory</TabsTrigger>
        </TabsList>

        <TabsContent value="org" className="mt-0">
          <OrgChart directory={directory} />
        </TabsContent>

        <TabsContent value="dir" className="mt-0">
          {directoryBody}

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-16 text-center">
            <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground font-body">No team members found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-10">
          {departments.map((dept) => {
            const members = filtered.filter((d) => d.department === dept);
            return (
              <div key={dept}>
                <div className="flex items-center gap-3 mb-4">
                  <Briefcase className="w-5 h-5 text-teal" />
                  <h2 className="font-display text-lg text-foreground">{dept}</h2>
                  <Badge variant="outline" className="text-[10px] font-body">{members.length}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {members.map((person) => {
                    const initials = getInitials(person.full_name);
                    const localTime = getLocalTime(person.timezone);

                    return (
                      <Card
                        key={person.id}
                        className="hover:shadow-md hover:border-teal/20 transition-all cursor-pointer group"
                        onClick={() => setSelectedPerson(person)}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center gap-4 mb-3">
                          <div className="relative">
                            <Avatar className="h-14 w-14 ring-2 ring-teal/10 group-hover:ring-teal/30 transition-all">
                              {person.avatar_url ? (
                                <AvatarImage src={person.avatar_url} alt={person.full_name} />
                              ) : null}
                              <AvatarFallback className="bg-gradient-to-br from-teal/20 to-teal/5 text-teal font-display text-lg">
                                {initials}
                              </AvatarFallback>
                            </Avatar>
                            {person.isOnboarded && (
                              <span className="absolute -bottom-1 -right-1 bg-green-500 text-white text-[7px] font-bold px-1.5 py-0.5 rounded-full leading-none tracking-wide border-2 border-background">
                                ONBOARDED
                              </span>
                            )}
                          </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-display text-sm text-foreground truncate group-hover:text-teal transition-colors">
                                {person.full_name}
                              </h3>
                              <p className="text-muted-foreground font-body text-xs truncate">
                                {person.job_title}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-1.5 text-xs font-body text-muted-foreground">
                            {person.location && (
                              <div className="flex items-center gap-2">
                                <MapPin className="w-3 h-3 flex-shrink-0 text-muted-foreground/50" />
                                <span className="truncate">{person.location}</span>
                              </div>
                            )}
                            {localTime && person.timezone && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3 flex-shrink-0 text-muted-foreground/50" />
                                <span className="truncate">{localTime} ({person.timezone.replace(/_/g, " ")})</span>
                              </div>
                            )}
                          </div>

                          <div className="mt-3 flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">{person.department}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
        </TabsContent>
      </Tabs>

      {/* Profile Dialog */}
      <Dialog open={!!selectedPerson} onOpenChange={(open) => !open && setSelectedPerson(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedPerson && (
            <>
              <DialogHeader>
                <DialogTitle className="sr-only">{selectedPerson.full_name} Profile</DialogTitle>
              </DialogHeader>

              {/* Header section */}
              <div className="flex flex-col items-center text-center pb-4 border-b border-border">
                <div className="relative mb-4">
                  <Avatar className="h-24 w-24 ring-4 ring-teal/10">
                    {selectedPerson.avatar_url ? (
                      <AvatarImage src={selectedPerson.avatar_url} alt={selectedPerson.full_name} />
                    ) : null}
                    <AvatarFallback className="bg-gradient-to-br from-teal/20 to-teal/5 text-teal font-display text-2xl">
                      {getInitials(selectedPerson.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  {selectedPerson.isOnboarded && (
                    <span className="absolute -bottom-1 -right-1 bg-green-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full leading-none tracking-wide border-2 border-background">
                      ONBOARDED
                    </span>
                  )}
                </div>
                <h2 className="font-display text-xl text-foreground">{selectedPerson.full_name}</h2>
                <p className="text-sm text-muted-foreground font-body">{selectedPerson.job_title}</p>
                <Badge variant="secondary" className="mt-2 text-xs">{selectedPerson.department}</Badge>
              </div>

              {/* Details */}
              <div className="space-y-4 py-4">
                {selectedPerson.bio && (
                  <div>
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">About</p>
                    <p className="text-sm font-body text-foreground leading-relaxed">{selectedPerson.bio}</p>
                  </div>
                )}

                <div className="grid gap-3">
                  {selectedPerson.location && (
                    <div className="flex items-center gap-3 text-sm font-body">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="text-foreground">{selectedPerson.location}</p>
                      </div>
                    </div>
                  )}

                  {selectedPerson.phone && (
                    <div className="flex items-center gap-3 text-sm font-body">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <a href={`tel:${selectedPerson.phone}`} className="text-foreground hover:text-teal transition-colors">
                          {selectedPerson.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {selectedPerson.timezone && (
                    <div className="flex items-center gap-3 text-sm font-body">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Time Zone</p>
                        <p className="text-foreground">
                          {selectedPerson.timezone.replace(/_/g, " ")}
                          {(() => {
                            const t = getLocalTime(selectedPerson.timezone);
                            return t ? ` — ${t} local time` : "";
                          })()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </IntranetLayout>
  );
};

export default EmployeeDirectory;
